import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { Clock, Trophy, XCircle, TrendingUp, ArrowLeft, Loader2, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_COLORS = {
  prospecting: "#0ea5e9",
  qualifying: "#8b5cf6",
  analysis: "#f59e0b",
  proposal: "#10b981",
  negotiation: "#ec4899",
  closed_win: "#059669",
  closed_discard: "#94a3b8",
};

/**
 * DealflowTimelinePage — cuánto tarda cada entidad del pipeline desde Prospecting a Closed WIN.
 * Muestra: KPIs globales, barras horizontales apiladas (una por entidad) con tiempo por stage.
 */
export default function DealflowTimelinePage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | win | in_progress | discard

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ecosystem/dealflow/timeline`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const filtered = useMemo(() => {
    const list = data?.entities || [];
    if (filter === "win") return list.filter(e => e.closed_win);
    if (filter === "in_progress") return list.filter(e => !e.is_closed);
    if (filter === "discard") return list.filter(e => e.current_stage === "closed_discard");
    return list;
  }, [data, filter]);

  const maxDays = useMemo(() => {
    return Math.max(30, ...(filtered.map(e => e.total_days || 0)));
  }, [filtered]);

  return (
    <div className="p-6 space-y-4" data-testid="dealflow-timeline-page">
      <div className="flex items-center gap-2 mb-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/partners?tab=dealflow")} className="gap-1">
          <ArrowLeft className="w-4 h-4"/> Volver al pipeline
        </Button>
      </div>

      {/* Hero */}
      <div className="rounded-3xl p-6 md:p-8 text-white" style={{ background: "linear-gradient(135deg, #0f172a 0%, #7c3aed 60%, #059669 100%)" }}>
        <div className="flex items-center gap-2 text-emerald-200 text-xs uppercase tracking-widest font-semibold mb-1">
          <Clock className="w-4 h-4"/> Timeline Concept-to-Initiative
        </div>
        <h1 className="text-3xl md:text-4xl font-black">¿Cuánto tardan las entidades desde concepto hasta Closed WIN?</h1>
        <p className="text-emerald-100/85 mt-2 max-w-3xl text-sm">Cada barra muestra el tiempo total desde <strong>concept_start_date</strong> y su distribución por etapa del pipeline. Optimiza el time-to-initiative acortando las etapas más lentas.</p>
      </div>

      {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600"/></div>}

      {data && !loading && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="timeline-kpis">
            <KPI icon={Trophy} label="Media días a WIN" value={data.stats.avg_days_to_win} suffix="días" color="#059669" testid="kpi-avg-win"/>
            <KPI icon={TrendingUp} label="Closed WIN" value={data.stats.count_closed_win} color="#10b981" testid="kpi-count-win"/>
            <KPI icon={Clock} label="En progreso" value={data.stats.count_in_progress} color="#f59e0b" testid="kpi-count-progress"/>
            <KPI icon={XCircle} label="Descartados" value={data.stats.count_closed_discard} color="#94a3b8" testid="kpi-count-discard"/>
          </div>

          {/* Avg per stage */}
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="timeline-avg-stage">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Media de días por etapa (todas las entidades)</div>
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
              {Object.entries(data.stats.avg_days_by_stage).map(([stage, days]) => (
                <div key={stage} className="text-center" data-testid={`avg-stage-${stage}`}>
                  <div className="text-[10px] uppercase font-bold mb-1" style={{color: STAGE_COLORS[stage]}}>{stage.replace(/_/g, " ")}</div>
                  <div className="text-2xl font-black">{days}</div>
                  <div className="text-[9px] text-muted-foreground">días</div>
                </div>
              ))}
            </div>
          </div>

          {/* Filter */}
          <div className="flex flex-wrap gap-2 items-center" data-testid="timeline-filters">
            <span className="text-xs font-bold text-muted-foreground">Filtrar:</span>
            {[
              { k: "all", l: `Todos (${data.entities.length})` },
              { k: "win", l: `Closed WIN (${data.stats.count_closed_win})` },
              { k: "in_progress", l: `En progreso (${data.stats.count_in_progress})` },
              { k: "discard", l: `Descartados (${data.stats.count_closed_discard})` },
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setFilter(opt.k)}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${filter === opt.k ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                data-testid={`timeline-filter-${opt.k}`}
              >
                {opt.l}
              </button>
            ))}
          </div>

          {/* Timeline bars */}
          <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-testid="timeline-list">
            {!filtered.length && (
              <div className="text-center text-muted-foreground text-sm py-6">Sin entidades con este filtro.</div>
            )}
            {filtered.map((e, i) => (
              <div key={e.id} className="space-y-1" data-testid={`timeline-row-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                  <span className="text-xs font-bold truncate max-w-[240px]" title={e.name}>{e.name}</span>
                  <span className="text-[10px] text-muted-foreground">{e.entity_type} · {e.country || "—"}</span>
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full font-bold text-white flex-shrink-0"
                    style={{background: STAGE_COLORS[e.current_stage] || "#94a3b8"}}
                  >
                    {(e.current_stage || "—").replace(/_/g, " ")}
                  </span>
                  <span className="ml-auto text-xs font-bold" style={{color: e.closed_win ? "#059669" : "#334155"}}>
                    {e.total_days ?? "—"} días
                    {e.closed_win && <Trophy className="w-3 h-3 inline ml-1 text-amber-500"/>}
                  </span>
                </div>
                {/* Stacked bar */}
                <div className="h-3 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800 flex" title="Tiempo total por etapa">
                  {Object.entries(e.stage_durations || {}).map(([stage, days]) => {
                    const pct = ((days || 0) / maxDays) * 100;
                    if (pct < 0.5) return null;
                    return (
                      <div
                        key={stage}
                        className="h-full"
                        style={{ width: `${pct}%`, background: STAGE_COLORS[stage] || "#cbd5e1" }}
                        title={`${stage.replace(/_/g, " ")}: ${days} días`}
                        data-testid={`timeline-bar-${i}-${stage}`}
                      />
                    );
                  })}
                </div>
                {/* Details on hover */}
                {Object.keys(e.stage_durations || {}).length > 0 && (
                  <div className="flex flex-wrap gap-1 text-[9px] text-muted-foreground pl-5">
                    {Object.entries(e.stage_durations).map(([stage, days]) => (
                      <span key={stage} className="flex items-center gap-0.5">
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{background: STAGE_COLORS[stage]}}/>
                        {stage.slice(0, 4)} <strong>{days}d</strong>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, suffix, color, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{color}}/>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
      </div>
      <div className="text-3xl font-black" style={{color}}>{value}{suffix ? <span className="text-sm text-muted-foreground ml-1">{suffix}</span> : null}</div>
    </div>
  );
}
